/**
 * Room Processing Utilities
 * Provides color mapping, type detection, and processing functions for rooms
 * Matches the Python implementation's room visualization
 */

import { Room } from '@/types/floorPlanViewer';

/**
 * Room type color mapping - matches Python visualization
 * These colors provide good contrast and visibility
 */
export const ROOM_TYPE_COLORS: Record<string, string> = {
  // Primary room types
  'bedroom': '#FF6B6B',      // Red
  'master_bedroom': '#E74C3C', // Darker Red
  'bathroom': '#4ECDC4',     // Teal
  'kitchen': '#45B7D1',      // Blue
  'living_room': '#FFA07A',  // Light Salmon
  'dining_room': '#98D8C8',  // Mint
  'office': '#F7DC6F',       // Yellow
  'hallway': '#BB8FCE',      // Purple
  'garage': '#85929E',       // Gray
  'closet': '#F8B739',       // Orange
  'laundry': '#52B788',      // Green
  'balcony': '#6C63FF',      // Indigo
  'stairs': '#A569BD',       // Purple
  'foyer': '#F1948A',        // Light Red
  'pantry': '#82E0AA',       // Light Green
  'storage': '#F0B27A',      // Light Orange
  'utility': '#85C1E2',      // Light Blue
  'guest_bedroom': '#F9A826', // Orange
  'ensuite': '#5DADE2',      // Sky Blue
  'powder_room': '#AED6F1',  // Light Blue
  'walk_in_closet': '#F4D03F', // Gold
  'mudroom': '#A9DFBF',      // Pale Green
  'den': '#C39BD3',          // Light Purple
  'family_room': '#FAD7A0',  // Peach
  'sunroom': '#FCF3CF',      // Light Yellow
  'basement': '#707B7C',     // Dark Gray
  'attic': '#95A5A6',        // Medium Gray
  'gym': '#EC7063',          // Coral
  'library': '#935116',      // Brown
  'media_room': '#4A235A',   // Dark Purple
  'wine_cellar': '#922B21',  // Wine Red
  'unknown': '#D3D3D3'       // Light Gray
};

/**
 * Extract room type from label using keyword matching
 * Matches Python's room type extraction logic
 */
export function extractRoomType(label: string): string {
  const lowerLabel = label.toLowerCase();
  
  const roomTypeKeywords: Record<string, string[]> = {
    'master_bedroom': ['master bedroom', 'master bed', 'primary bedroom', 'primary suite'],
    'bedroom': ['bedroom', 'bed room', 'guest bedroom', 'child bedroom', 'kids room'],
    'bathroom': ['bathroom', 'bath', 'toilet', 'wc', 'washroom', 'restroom', 'powder room'],
    'ensuite': ['ensuite', 'en-suite', 'en suite'],
    'kitchen': ['kitchen', 'cooking area', 'cooking space'],
    'pantry': ['pantry', 'food storage'],
    'living_room': ['living room', 'living', 'lounge', 'sitting room', 'great room'],
    'family_room': ['family room', 'family area'],
    'dining_room': ['dining room', 'dining', 'eating area'],
    'office': ['office', 'study', 'workspace', 'home office', 'work room'],
    'den': ['den', 'library', 'reading room'],
    'garage': ['garage', 'parking', 'carport', 'car port'],
    'hallway': ['hallway', 'corridor', 'passage', 'hall'],
    'foyer': ['foyer', 'entryway', 'entrance', 'entry', 'vestibule'],
    'walk_in_closet': ['walk-in closet', 'walkin closet', 'walk in'],
    'closet': ['closet', 'wardrobe', 'storage room'],
    'laundry': ['laundry', 'utility room', 'washing'],
    'mudroom': ['mudroom', 'mud room'],
    'balcony': ['balcony', 'terrace', 'patio', 'deck'],
    'stairs': ['stairs', 'staircase', 'stairwell', 'steps'],
    'basement': ['basement', 'cellar'],
    'attic': ['attic', 'loft'],
    'gym': ['gym', 'exercise', 'fitness', 'workout'],
    'media_room': ['media room', 'theater', 'cinema', 'tv room'],
    'sunroom': ['sunroom', 'sun room', 'solarium', 'conservatory'],
    'wine_cellar': ['wine cellar', 'wine room', 'wine storage']
  };
  
  // Check each room type's keywords
  for (const [type, keywords] of Object.entries(roomTypeKeywords)) {
    for (const keyword of keywords) {
      if (lowerLabel.includes(keyword)) {
        return type;
      }
    }
  }
  
  return 'unknown';
}

/**
 * Get color for a room type
 */
export function getRoomTypeColor(roomType: string | undefined | null): string {
  if (!roomType) return ROOM_TYPE_COLORS.unknown;
  return ROOM_TYPE_COLORS[roomType] || ROOM_TYPE_COLORS.unknown;
}

/**
 * Format room label for display
 */
export function formatRoomLabel(room: Room): string {
  if (room.name) return room.name;
  
  const type = room.room_type || room.class || 'Room';
  const formattedType = type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  
  return formattedType;
}

/**
 * Extract room features from label
 */
export function extractRoomFeatures(label: string): string[] {
  const features: string[] = [];
  const lowerLabel = label.toLowerCase();
  
  // Size features
  if (lowerLabel.includes('master') || lowerLabel.includes('large')) {
    features.push('large');
  }
  if (lowerLabel.includes('small') || lowerLabel.includes('compact')) {
    features.push('small');
  }
  
  // Location features
  if (lowerLabel.includes('corner')) features.push('corner');
  if (lowerLabel.includes('upper') || lowerLabel.includes('upstairs')) {
    features.push('upper-floor');
  }
  if (lowerLabel.includes('lower') || lowerLabel.includes('downstairs')) {
    features.push('lower-floor');
  }
  
  // Special features
  if (lowerLabel.includes('walk-in') || lowerLabel.includes('walkin')) {
    features.push('walk-in');
  }
  if (lowerLabel.includes('ensuite') || lowerLabel.includes('en-suite')) {
    features.push('ensuite');
  }
  if (lowerLabel.includes('guest')) features.push('guest');
  if (lowerLabel.includes('private')) features.push('private');
  if (lowerLabel.includes('shared')) features.push('shared');
  if (lowerLabel.includes('open')) features.push('open-plan');
  
  return features;
}

/**
 * Calculate room area from polygon points
 * Uses the Shoelace formula
 */
export function calculatePolygonArea(points: { x: number; y: number }[]): number {
  if (points.length < 3) return 0;
  
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  
  return Math.abs(area / 2);
}

/**
 * Calculate center point of a polygon
 */
export function calculatePolygonCenter(points: { x: number; y: number }[]): { x: number; y: number } {
  if (points.length === 0) return { x: 0, y: 0 };
  
  let cx = 0;
  let cy = 0;
  let area = 0;
  
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    const a = points[i].x * points[j].y - points[j].x * points[i].y;
    area += a;
    cx += (points[i].x + points[j].x) * a;
    cy += (points[i].y + points[j].y) * a;
  }
  
  if (area === 0) {
    // Fallback to simple average for degenerate polygons
    const sumX = points.reduce((sum, p) => sum + p.x, 0);
    const sumY = points.reduce((sum, p) => sum + p.y, 0);
    return { x: sumX / points.length, y: sumY / points.length };
  }
  
  area *= 3;
  return { x: cx / area, y: cy / area };
}

/**
 * Generate a color palette for room visualization
 * Returns an array of distinct colors
 */
export function generateRoomColorPalette(): string[] {
  return [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#FFA07A', // Light Salmon
    '#98D8C8', // Mint
    '#F7DC6F', // Yellow
    '#BB8FCE', // Purple
    '#85929E', // Gray
    '#F8B739', // Orange
    '#52B788', // Green
    '#6C63FF', // Indigo
    '#F1948A', // Light Red
    '#82E0AA', // Light Green
    '#85C1E2', // Light Blue
    '#F9A826', // Orange
    '#5DADE2', // Sky Blue
  ];
}

/**
 * Get a color from the palette by index
 */
export function getRoomColorByIndex(index: number): string {
  const palette = generateRoomColorPalette();
  return palette[index % palette.length];
}

/**
 * Check if a point is inside a polygon
 */
export function isPointInPolygon(point: { x: number; y: number }, polygon: { x: number; y: number }[]): boolean {
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    
    const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  return inside;
}

/**
 * Calculate the distance from a point to a polygon edge
 */
export function distanceToPolygon(point: { x: number; y: number }, polygon: { x: number; y: number }[]): number {
  let minDistance = Infinity;
  
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const distance = distanceToLineSegment(point, polygon[i], polygon[j]);
    minDistance = Math.min(minDistance, distance);
  }
  
  return minDistance;
}

/**
 * Calculate distance from a point to a line segment
 */
function distanceToLineSegment(
  point: { x: number; y: number },
  lineStart: { x: number; y: number },
  lineEnd: { x: number; y: number }
): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  
  if (dx === 0 && dy === 0) {
    // Line segment is a point
    return Math.sqrt(
      Math.pow(point.x - lineStart.x, 2) + 
      Math.pow(point.y - lineStart.y, 2)
    );
  }
  
  // Calculate the t that minimizes the distance
  const t = Math.max(0, Math.min(1, 
    ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / 
    (dx * dx + dy * dy)
  ));
  
  // Find the nearest point on the line segment
  const nearestX = lineStart.x + t * dx;
  const nearestY = lineStart.y + t * dy;
  
  // Calculate distance to that point
  return Math.sqrt(
    Math.pow(point.x - nearestX, 2) + 
    Math.pow(point.y - nearestY, 2)
  );
}